import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService } from '@sym-ui/toolkit3/api';
import { ButtonModule } from '@sym-ui/toolkit3/button';
import { ConfirmDialogModule } from '@sym-ui/toolkit3/confirmdialog';
import { DrawerModule } from '@sym-ui/toolkit3/drawer';
import { Message } from '@sym-ui/toolkit3/message';
import { ToolbarModule } from '@sym-ui/toolkit3/toolbar';

import {
  NotificationInstance,
  NotificationSetting,
  NotificationTableRow,
} from '../../notifications.model';

@Component({
  selector: 'casb-notifications-details',
  standalone: true,
  templateUrl: './notifications-details.component.html',
  styleUrl: './notifications-details.component.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [ButtonModule, ConfirmDialogModule, DrawerModule, FormsModule, Message, ToolbarModule],
  providers: [ConfirmationService],
})
export class NotificationsDetailsComponent implements OnChanges {
  private readonly confirmationService = inject(ConfirmationService);

  /** Maximum notification recipients allowed per SpanVA instance. */
  readonly maxRecipients = 10;

  @Input() row!: NotificationTableRow;
  @Input() instance!: NotificationInstance;
  /** Pre-built settings list produced by `NotificationsState.buildFlyoutSettings`. */
  @Input() settings: NotificationSetting[] = [];
  @Input() isHidden = true;
  @Input() savePending = false;
  @Input() allowedDomains: string[] = [];
  @Output() isHiddenChange = new EventEmitter<boolean>();
  @Output() flyoutClose = new EventEmitter<void>();
  @Output() save = new EventEmitter<{
    spanvaId: string;
    settings: NotificationSetting[];
    recipients: string[];
    masterEnabled: boolean;
  }>();

  notificationSettings: NotificationSetting[] = [];
  masterEnabled = true;
  recipientEmail = '';
  recipientEmailError = '';
  recipients: string[] = [];
  recipientErrors: string[] = [];
  showNewRecipientInput = false;
  editingRecipientIndex: number | null = null;
  editingRecipientEmail = '';
  editingRecipientEmailError = '';

  private originalSettings: NotificationSetting[] = [];
  private originalRecipients: string[] = [];
  private originalMasterEnabled = true;

  get isSaveEnabled(): boolean {
    return this.isDirty && this.isValid && !this.savePending;
  }

  /** True when the recipient list has reached {@link maxRecipients}. */
  get isRecipientsLimitReached(): boolean {
    return this.recipients.length >= this.maxRecipients;
  }

  /** True when settings or recipients differ from last loaded / saved snapshot. */
  get hasUnsavedChanges(): boolean {
    return this.isDirty;
  }

  private get isDirty(): boolean {
    const settingsChanged = this.notificationSettings.some((s, i) => {
      const orig = this.originalSettings[i];
      return s.enabled !== orig?.enabled || s.frequency !== orig?.frequency;
    });
    const recipientsChanged =
      this.recipients.length !== this.originalRecipients.length ||
      this.recipients.some((r, i) => r !== this.originalRecipients[i]);
    const masterChanged = this.masterEnabled !== this.originalMasterEnabled;
    return settingsChanged || recipientsChanged || masterChanged;
  }

  private get isValid(): boolean {
    const allFrequenciesSet = this.notificationSettings
      .filter((s) => s.frequency_mode !== 'na' && s.enabled)
      .every((s) => !!s.frequency?.trim());
    const hasRecipients =
      this.recipients.length > 0 && this.recipients.every((r) => !this.validateEmailDomain(r));
    return allFrequenciesSet && hasRecipients;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['settings'] || changes['instance']) && this.instance) {
      const cloned = this.cloneSettings(this.settings);
      const recipients = this.instance.recipients?.length
        ? [...this.instance.recipients]
        : ['sys-admin@domain.com'];
      const masterEnabled = this.instance.master_enabled;

      this.notificationSettings = cloned;
      this.recipients = recipients;
      this.masterEnabled = masterEnabled;

      this.originalSettings = this.cloneSettings(cloned);
      this.originalRecipients = [...recipients];
      this.originalMasterEnabled = masterEnabled;

      this.recipientEmail = '';
      this.recipientEmailError = '';
      this.showNewRecipientInput = false;
      this.editingRecipientIndex = null;
      this.editingRecipientEmail = '';
      this.editingRecipientEmailError = '';
    }
    if (changes['allowedDomains']) {
      this.revalidateRecipients();
    }
  }

  isNaFrequency(setting: NotificationSetting): boolean {
    return setting.frequency_mode === 'na';
  }

  onClose(): void {
    this.isHiddenChange.emit(true);
    this.flyoutClose.emit();
  }

  /** Cancel from the footer: prompts if there are unsaved edits, otherwise closes. */
  onCancelClick(): void {
    if (this.savePending) {
      return;
    }
    if (this.hasUnsavedChanges) {
      this.confirmationService.confirm({
        message: 'You have unsaved changes. Are you sure you want to navigate away from this page?',
        icon: 'warn',
        rejectButtonProps: {
          label: 'No',
          severity: 'secondary',
          outlined: true,
        },
        acceptButtonProps: {
          label: 'Yes',
          severity: 'primary',
        },
        accept: () => this.onCancel(),
      });
      return;
    }
    this.onCancel();
  }

  onCancel(): void {
    this.notificationSettings = this.cloneSettings(this.originalSettings);
    this.recipients = [...this.originalRecipients];
    this.masterEnabled = this.originalMasterEnabled;
    this.recipientEmail = '';
    this.recipientEmailError = '';
    this.showNewRecipientInput = false;
    this.editingRecipientIndex = null;
    this.editingRecipientEmail = '';
    this.editingRecipientEmailError = '';
    this.onClose();
  }

  onSave(): void {
    const settings = this.cloneSettings(this.notificationSettings);
    const recipients = [...this.recipients];
    const masterEnabled = this.masterEnabled;
    this.save.emit({ spanvaId: this.instance.spanva_id, settings, recipients, masterEnabled });
  }

  addRecipientRow(): void {
    if (this.isRecipientsLimitReached) {
      return;
    }
    this.showNewRecipientInput = true;
    this.recipientEmail = '';
  }

  confirmNewRecipient(): void {
    if (this.recipients.length >= this.maxRecipients) {
      return;
    }
    const email = this.recipientEmail.trim();
    const error = this.validateEmailDomain(email);
    if (error) {
      this.recipientEmailError = error;
      return;
    }
    this.recipients = [...this.recipients, email];
    this.recipientEmail = '';
    this.recipientEmailError = '';
    this.showNewRecipientInput = false;
    this.revalidateRecipients();
  }

  cancelNewRecipient(): void {
    this.recipientEmail = '';
    this.recipientEmailError = '';
    this.showNewRecipientInput = false;
  }

  startEditRecipient(index: number): void {
    this.editingRecipientIndex = index;
    this.editingRecipientEmail = this.recipients[index];
    this.editingRecipientEmailError = '';
  }

  confirmEditRecipient(): void {
    if (this.editingRecipientIndex === null) return;
    const email = this.editingRecipientEmail.trim();
    const error = this.validateEmailDomain(email);
    if (error) {
      this.editingRecipientEmailError = error;
      return;
    }
    const updated = [...this.recipients];
    updated[this.editingRecipientIndex] = email;
    this.recipients = updated;
    this.editingRecipientIndex = null;
    this.editingRecipientEmail = '';
    this.editingRecipientEmailError = '';
    this.revalidateRecipients();
  }

  cancelEditRecipient(): void {
    this.editingRecipientIndex = null;
    this.editingRecipientEmail = '';
    this.editingRecipientEmailError = '';
  }

  private validateEmailDomain(email: string): string {
    if (!email) {
      return 'Email address is required.';
    }
    const atIndex = email.lastIndexOf('@');
    if (atIndex < 1 || atIndex === email.length - 1) {
      return 'Enter a valid email address.';
    }
    if (!this.allowedDomains.length) {
      return '';
    }
    const domain = email.slice(atIndex + 1).toLowerCase();
    const allowed = this.allowedDomains.map((d) => d.toLowerCase());
    if (!allowed.includes(domain)) {
      return `Domain must be one of: ${this.allowedDomains.join(', ')}.`;
    }
    return '';
  }

  removeRecipient(index: number): void {
    this.recipients = this.recipients.filter((_, i) => i !== index);
    this.revalidateRecipients();
  }

  trackByUiId(_index: number, setting: NotificationSetting): string {
    return setting.ui_id;
  }

  private revalidateRecipients(): void {
    this.recipientErrors = this.recipients.map((r) => this.validateEmailDomain(r));
  }

  private cloneSettings(settings: NotificationSetting[]): NotificationSetting[] {
    return settings.map((s) => ({ ...s }));
  }
}
